import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DashboardService } from '../../../services/dashboard.service';
import {
  AnalyticsPeriod,
  ProfessorAnalyticsBundle,
  OverviewTheme,
  OverviewQuestion,
  MediaConsumptionRow
} from '../models/analytics.types';
import { PROFESSOR_ANALYTICS_MOCK_BUNDLE } from './professor-analytics-mock-data.const';

// Single source of truth for analytics data fetching.
//
// Today: derives Overview rows from the existing pathway/comments
// endpoints and falls back to a static demo bundle for everything
// else. The contract below is what the backend should implement
// (see API_CONTRACT.md) — only `fetchBundle()` should change when
// real endpoints land; tabs stay untouched.
@Injectable({ providedIn: 'root' })
export class ProfessorAnalyticsService {
  constructor(private dashboardService: DashboardService) {}

  // ────────────────────────────────────────────────────────────────
  // Public API — what tabs and the parent orchestrator consume.
  // ────────────────────────────────────────────────────────────────

  fetchBundle(pathwayId: string, _period: AnalyticsPeriod): Observable<ProfessorAnalyticsBundle> {
    if (!pathwayId || pathwayId === 'local-pathway') {
      return of(this.buildMockBundle());
    }
    return new Observable<ProfessorAnalyticsBundle>((subscriber) => {
      this.dashboardService.getPathwayDetail(pathwayId).subscribe(
        (response: any) => {
          const pathway = (response && response.data) || null;
          if (!pathway) {
            subscriber.next(this.buildMockBundle());
            subscriber.complete();
            return;
          }
          this.buildBundleFromPathway(pathway, pathwayId).subscribe(
            (bundle) => { subscriber.next(bundle); subscriber.complete(); },
            ()       => { subscriber.next(this.buildMockBundle()); subscriber.complete(); }
          );
        },
        () => { subscriber.next(this.buildMockBundle()); subscriber.complete(); }
      );
    });
  }

  // Mock helper exposed so the parent component can fall back without
  // re-importing the constant.
  buildMockBundle(): ProfessorAnalyticsBundle {
    return JSON.parse(JSON.stringify(PROFESSOR_ANALYTICS_MOCK_BUNDLE));
  }

  // ────────────────────────────────────────────────────────────────
  // Authorization helper preserved from original component.
  // ────────────────────────────────────────────────────────────────

  canViewProfessorAnalytics(pathway: any, currentUser: any): boolean {
    if (!pathway) { return false; }
    if (!currentUser) { return true; }
    const role = String(currentUser.role || '').toUpperCase();
    if (role === 'SUPER_USER' || role === 'CURATOR' || role === 'PROFESSOR') { return true; }
    return currentUser._id === pathway.createdBy;
  }

  // ────────────────────────────────────────────────────────────────
  // Pathway-driven Overview derivation (preserves existing behavior).
  // Public so the parent component can run the auth check before
  // building the bundle.
  // ────────────────────────────────────────────────────────────────

  buildBundleFromPathway(pathway: any, pathwayId: string): Observable<ProfessorAnalyticsBundle> {
    const mediaList = Array.isArray(pathway && pathway.playlists) ? pathway.playlists : [];
    if (!mediaList.length) {
      return of(this.buildMockBundle());
    }

    const requests = mediaList.map((media: any) => {
      const videoId = media && media._id ? media._id : '';
      if (!videoId) { return of({ videoId: '', media, comments: [] as any[] }); }
      return this.dashboardService.getVideoCommentsList(
        { videoId, type: 'all', page: 1, limit: 100, isFlagged: false, pathwayId, timeSlot: 0 },
        'all',
        'private'
      ).pipe(
        map((res: any) => ({ videoId, media, comments: (res && res.data) || [] })),
        catchError(() => of({ videoId, media, comments: [] as any[] }))
      );
    });

    return forkJoin(requests).pipe(
      map((commentRecords: any[]) => {
        const students = this.collectStudentsFromComments(commentRecords);
        const mediaRows = this.buildMediaConsumptionRows(students, commentRecords);
        const topThemes = this.extractTopThemes(commentRecords, pathway);
        const questionInsights = this.buildQuestionInsights(commentRecords);

        // Take the rest from mock for now; per-tab endpoints will
        // replace these slices in API_CONTRACT.md Phase 3.
        const base = this.buildMockBundle();
        return {
          ...base,
          mediaRows,
          topThemes: topThemes.length ? topThemes : base.topThemes,
          questionInsights: questionInsights.length ? questionInsights : base.questionInsights
        };
      })
    );
  }

  // ────────────────────────────────────────────────────────────────
  // Comment-derived helpers (carried over from original component).
  // ────────────────────────────────────────────────────────────────

  private collectStudentsFromComments(commentRecords: any[]): any[] {
    const userMap: { [k: string]: any } = {};
    (commentRecords || []).forEach((record: any) => {
      (record.comments || []).forEach((comment: any) => {
        const user = comment && (comment.user || comment.userId) ? (comment.user || comment.userId) : null;
        const id = user && user._id ? user._id : '';
        if (!id || userMap[id]) { return; }
        userMap[id] = {
          _id: id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          screenName: user.screenName || user.name || 'Student'
        };
      });
    });
    return Object.keys(userMap).map((k) => userMap[k]);
  }

  private buildMediaConsumptionRows(students: any[], commentRecords: any[]): MediaConsumptionRow[] {
    const rows: MediaConsumptionRow[] = [];
    (commentRecords || []).forEach((record: any) => {
      const media = record.media || {};
      const comments = record.comments || [];
      const mediaType = this.resolveMediaType(media);
      const countByUser: { [k: string]: number } = {};
      comments.forEach((c: any) => {
        const owner = (c && c.user && c.user._id) || c.userId || '';
        if (owner) { countByUser[owner] = (countByUser[owner] || 0) + 1; }
      });
      (students || []).forEach((student: any) => {
        const id = student && student._id ? student._id : '';
        const count = id ? (countByUser[id] || 0) : 0;
        rows.push({
          mediaId: media._id,
          mediaTitle: media.title || 'Untitled media',
          mediaType,
          studentName:
            `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
            student.screenName ||
            'Student',
          engaged: count > 0,
          commentCount: count
        });
      });
    });
    return rows;
  }

  private resolveMediaType(media: any): string {
    const platform = String(media && media.platForm ? media.platForm : '').toLowerCase();
    const tagName = String(media && media.tag && media.tag.name ? media.tag.name : '').toLowerCase();
    const videoUrl = String(media && media.videoURL ? media.videoURL : '').toLowerCase();
    if (platform === 'youtube') { return 'video'; }
    if (tagName.indexOf('pdf') > -1 || videoUrl.indexOf('.pdf') > -1) { return 'pdf'; }
    return 'article';
  }

  private extractTopThemes(commentRecords: any[], pathway: any): OverviewTheme[] {
    const baseText: string[] = [];
    if (pathway && pathway.description) { baseText.push(pathway.description); }
    ((pathway && pathway.upskills) || []).forEach((up: any) => {
      if (up && up.title) { baseText.push(up.title); }
    });
    commentRecords.forEach((record: any) => {
      (record.comments || []).forEach((c: any) => {
        if (c && c.text) { baseText.push(c.text); }
      });
    });

    const stopWords: { [k: string]: boolean } = {
      the: true, and: true, for: true, that: true, this: true, with: true, from: true,
      are: true, was: true, were: true, your: true, about: true, what: true, when: true,
      how: true, into: true, just: true, more: true, can: true, could: true, should: true, would: true
    };
    const tokenCounts: { [k: string]: number } = {};
    const bigramCounts: { [k: string]: number } = {};

    baseText.forEach((text: string) => {
      const tokens = String(text || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((t: string) => t.length > 3 && !stopWords[t]);
      tokens.forEach((t: string) => { tokenCounts[t] = (tokenCounts[t] || 0) + 1; });
      for (let i = 0; i < tokens.length - 1; i++) {
        const bg = `${tokens[i]} ${tokens[i + 1]}`;
        bigramCounts[bg] = (bigramCounts[bg] || 0) + 1;
      }
    });

    const rankedBigrams = Object.keys(bigramCounts)
      .map((k) => ({ key: k, value: bigramCounts[k] }))
      .sort((a, b) => b.value - a.value);
    const rankedTokens = Object.keys(tokenCounts)
      .map((k) => ({ key: k, value: tokenCounts[k] }))
      .sort((a, b) => b.value - a.value);

    const themes: OverviewTheme[] = [];
    rankedBigrams.slice(0, 3).forEach((row) => {
      themes.push({
        name: this.titleCase(row.key),
        evidence: row.value,
        description: `Students repeatedly discuss ${row.key} across course interactions.`
      });
    });
    if (themes.length < 3) {
      rankedTokens.forEach((row) => {
        if (themes.length >= 3) { return; }
        if (!themes.some((t) => t.name.toLowerCase().indexOf(row.key) > -1)) {
          themes.push({
            name: this.titleCase(row.key),
            evidence: row.value,
            description: `${this.titleCase(row.key)} appears frequently in discussions.`
          });
        }
      });
    }
    return themes.slice(0, 3);
  }

  private buildQuestionInsights(commentRecords: any[]): OverviewQuestion[] {
    const questionTexts: string[] = [];
    commentRecords.forEach((record: any) => {
      (record.comments || []).forEach((c: any) => {
        const text = String(c && c.text ? c.text : '').trim();
        const isQ = String(c && c.type ? c.type : '').toUpperCase() === 'QUESTION';
        if (text && (isQ || text.indexOf('?') > -1)) { questionTexts.push(text); }
      });
    });
    return this.aggregateQuestionTopics(questionTexts);
  }

  private aggregateQuestionTopics(questionTexts: string[]): OverviewQuestion[] {
    const stopWords: { [k: string]: boolean } = {
      the: true, and: true, what: true, why: true, when: true, where: true,
      how: true, can: true, does: true, with: true, from: true, this: true
    };
    const grouped: { [k: string]: OverviewQuestion } = {};
    (questionTexts || []).forEach((q) => {
      const key = String(q || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w: string) => w.length > 3 && !stopWords[w])
        .slice(0, 5)
        .join(' ');
      if (!key) { return; }
      if (!grouped[key]) {
        grouped[key] = {
          topic: this.titleCase(key.split(' ').slice(0, 3).join(' ')),
          sample: q,
          count: 0
        };
      }
      grouped[key].count += 1;
    });
    return Object.keys(grouped)
      .map((k) => grouped[k])
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  private titleCase(value: string): string {
    return String(value || '')
      .split(' ')
      .map((w) => (w ? `${w[0].toUpperCase()}${w.slice(1)}` : ''))
      .join(' ');
  }
}
